// src/themeContext.js
import { createTheme } from '@mui/material/styles';
import { createContext, useState, useMemo } from 'react';

// --- Official ČVUT Brand Colors ---
const ctuPrimaryBlue = '#0065BD';   // Pantone 300
const ctuLightBlue = '#6AADE4';     // Pantone 284 (for dark mode primary actions)
const ctuTeal = '#156570';          // Pantone 5473
const ctuBrightTeal = '#00B2A9'; //

const lightThemePalette = {
  mode: 'light',
  primary: { main: ctuPrimaryBlue },
  secondary: { main: ctuTeal },
  background: {
    default: '#F4F6F8', // A very light grey, easier on the eyes than pure white
    paper: '#FFFFFF',   // Panels (like your sidebar cards) will be pure white
  },
  text: {
    primary: '#1A2027',   // A strong, dark color for text
    secondary: '#6C757D',
  }
};

const darkThemePalette = {
  mode: 'dark',
  primary: { main: ctuLightBlue },
  secondary: { main: ctuBrightTeal },
  background: {
    default: '#1A2027', // A deep, dark blue-grey for the main page background
    paper: '#262F3A',   // Your sidebar panels will use this slightly lighter color
  },
  text: {
    primary: '#E0E0E0',   // A very bright, readable text color
    secondary: '#A0AEC0', // A softer grey for secondary information
  }
};


// --- Theme Creation Logic ---
const createAppTheme = (mode) => createTheme({
  palette: mode === 'light' ? lightThemePalette : darkThemePalette,
  typography: {
    fontFamily: '"Technika", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },      // Will use Technika-Bold
    h6: { fontWeight: 500 },      // Will use Technika-Regular
    body1: { fontWeight: 400 },   // Will use Technika-Book
    caption: { fontWeight: 300 }, // Will use Technika-Light
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: (theme) => ({
          // --- For Webkit-based browsers (Chrome, Safari, Edge) ---
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            // Use the panel background color for the track
            background: theme.palette.background.paper,
          },
          '&::-webkit-scrollbar-thumb': {
            // Use a subtle color for the scrollbar thumb
            backgroundColor: theme.palette.mode === 'dark' ? '#555' : '#ccc',
            borderRadius: '10px',
            border: `2px solid ${theme.palette.background.paper}`,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            // Make it a bit more prominent on hover
            background: theme.palette.mode === 'dark' ? '#777' : '#aaa',
          },

          // --- For Firefox ---
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.mode === 'dark' ? '#555' : '#ccc'} ${theme.palette.background.paper}`,
        }),
      },
    },
  },
});

// --- Your existing Context and Hook ---
// (This part remains exactly the same)
export const ThemeModeContext = createContext({ toggleThemeMode: () => {} });

export const useThemeMode = () => {
  const [mode, setMode] = useState('dark');
  const themeMode = useMemo(() => ({
    toggleThemeMode: () => {
      setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    },
  }), []);
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  return [theme, themeMode];
};